import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { NavLink } from "react-router-dom";
import {
  Home,
  FileText,
  UserCircle,
  Users,
  Settings,
  LogOut,
  Mail,
  UserCheck,
  ChevronLeft,
  ChevronRight,
  FlaskConical,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { ThemeToggle } from "../ThemeToggle";
import { clearTokens } from "../../utils/tokenUtils";
import { redirectToAuthLogout } from "../../utils/authUtils";
import logo from "../../assets/logo-teal.svg";
import logoSymbol from "../../assets/logo-symbol-teal.svg";
import Footer from "../Footer";

interface AppSidebarProps {
  user?: any;
  showMatches?: boolean;
  showExperiments?: boolean;
  open?: boolean;
  onClose?: () => void;
  onCollapseChange?: (collapsed: boolean) => void;
}

// ============================================================================
// Styled Components - Desktop Sidebar
// ============================================================================

const DesktopSidebarShell = styled.aside<{ $collapsed: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: ${(props) => (props.$collapsed ? "64px" : "240px")};
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
  border-right: 1px solid rgba(148, 163, 184, 0.35);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  overflow-y: auto;
  overflow-x: visible;
  z-index: 900;
  transition: width 220ms ease;

  /* Custom scrollbar for elegance */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.3);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(148, 163, 184, 0.5);
  }

  @media (max-width: 1024px) {
    display: none;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const SidebarHeader = styled.div<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${(props) => (props.$collapsed ? "1.35rem 0.5rem" : "1.35rem 1rem")};
  min-height: 82px;
  transition: padding 220ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const LogoLink = styled(NavLink)`
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  position: relative;
`;

const LogoImage = styled.img<{ $collapsed: boolean; $show: "full" | "icon" }>`
  height: ${(props) => (props.$show === "icon" ? "42px" : "34px")};
  width: auto;
  opacity: ${(props) =>
    (props.$collapsed && props.$show === "icon") ||
    (!props.$collapsed && props.$show === "full")
      ? 1
      : 0};
  transition: opacity 220ms ease;
  position: ${(props) => (props.$show === "icon" ? "absolute" : "relative")};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const CollapseToggle = styled.button`
  position: fixed;
  top: 80px;
  left: 240px;
  transform: translateX(-50%);
  width: 28px;
  height: 28px;
  padding: 0;
  border-radius: 50%;
  background: #ffffff;
  color: #0A5F6F;
  border: 1px solid rgba(148, 163, 184, 0.4);
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.12), 0 1px 2px rgba(15, 23, 42, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 220ms ease;
  z-index: 901;

  &:hover {
    background: #0A5F6F;
    color: #ffffff;
    border-color: #0A5F6F;
    box-shadow: 0 4px 16px rgba(10, 95, 111, 0.3);
    transform: translateX(-50%) scale(1.1);
  }

  &:active {
    transform: translateX(-50%) scale(0.95);
  }

  svg {
    width: 16px;
    height: 16px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover {
      transform: translateX(-50%);
    }
    &:active {
      transform: translateX(-50%);
    }
  }
`;

const CollapseToggleCollapsed = styled(CollapseToggle)`
  left: 64px;
`;

const SidebarNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 0.5rem 0.85rem;
  flex: 1;
`;

const NavSection = styled.div`
  display: grid;
  gap: 0.5rem;
`;

const SidebarTitle = styled.div<{ $collapsed: boolean }>`
  font-size: 0.7rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #94a3b8;
  font-weight: 700;
  padding: ${(props) => (props.$collapsed ? "0" : "0 0.6rem")};
  opacity: ${(props) => (props.$collapsed ? 0 : 1)};
  height: ${(props) => (props.$collapsed ? "0" : "auto")};
  overflow: hidden;
  transition: opacity 150ms ease, height 150ms ease, padding 150ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const SidebarLink = styled(NavLink)<{ $index?: number }>`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.6rem 0.75rem;
  border-radius: 10px;
  color: #0f172a;
  font-weight: 600;
  font-size: 0.92rem;
  text-decoration: none;
  position: relative;
  transition: all 150ms ease;
  animation: slideIn 300ms ease ${(props) => (props.$index || 0) * 30}ms backwards;

  /* Active indicator - colored accent bar */
  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 0;
    background: #227b7e;
    border-radius: 0 2px 2px 0;
    transition: height 200ms ease;
  }

  svg {
    width: 18px;
    height: 18px;
    color: #227b7e;
    flex-shrink: 0;
    transition: transform 150ms ease;
  }

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:hover {
    background: rgba(34, 123, 126, 0.1);
    transform: translateX(2px);

    svg {
      transform: scale(1.1);
    }
  }

  &.active {
    background: rgba(34, 123, 126, 0.16);
    color: #0f4d4f;

    &::before {
      height: 60%;
    }
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(-10px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;

    &:hover {
      transform: none;
    }

    svg {
      transition: none;
    }

    &::before {
      transition: none;
    }
  }
`;

const NavLabel = styled.span<{ $collapsed: boolean }>`
  opacity: ${(props) => (props.$collapsed ? 0 : 1)};
  width: ${(props) => (props.$collapsed ? "0" : "auto")};
  overflow: hidden;
  transition: opacity 150ms ease, width 220ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const SidebarFooter = styled.div`
  margin-top: auto;
  padding: 1rem 0.85rem;
  border-top: 1px solid rgba(148, 163, 184, 0.2);
`;

const UserCard = styled.div<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: ${(props) => (props.$collapsed ? "0.5rem" : "0.75rem")};
  border-radius: 12px;
  background: ${(props) =>
    props.$collapsed
      ? "transparent"
      : "linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.8))"};
  backdrop-filter: ${(props) => (props.$collapsed ? "none" : "blur(10px)")};
  border: ${(props) =>
    props.$collapsed ? "none" : "1px solid rgba(148, 163, 184, 0.25)"};
  cursor: pointer;
  transition: all 150ms ease;
  justify-content: ${(props) => (props.$collapsed ? "center" : "flex-start")};

  &:hover {
    background: ${(props) =>
      props.$collapsed
        ? "rgba(34, 123, 126, 0.08)"
        : "linear-gradient(135deg, rgba(255, 255, 255, 1), rgba(248, 250, 252, 0.95))"};
    transform: ${(props) => (props.$collapsed ? "scale(1.05)" : "translateY(-1px)")};
    box-shadow: ${(props) =>
      props.$collapsed ? "none" : "0 4px 12px rgba(34, 123, 126, 0.12)"};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover {
      transform: none;
    }
  }
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #227b7e, #0f4d4f);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(34, 123, 126, 0.2);
`;

const UserInfo = styled.div<{ $collapsed: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  overflow: hidden;
  opacity: ${(props) => (props.$collapsed ? 0 : 1)};
  width: ${(props) => (props.$collapsed ? "0" : "auto")};
  transition: opacity 150ms ease, width 220ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const UserName = styled.div`
  font-weight: 700;
  font-size: 0.9rem;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserEmail = styled.div`
  font-size: 0.75rem;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const UserRole = styled.div`
  display: inline-block;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #227b7e;
  background: rgba(34, 123, 126, 0.12);
  padding: 0.15rem 0.4rem;
  border-radius: 4px;
  margin-top: 0.25rem;
  width: fit-content;
`;

const LogoutSection = styled.div`
  padding: 0 0.85rem 0.5rem;
  border-top: 1px solid rgba(148, 163, 184, 0.2);
  margin-top: 0.5rem;
`;

const LogoutButton = styled.button<{ $collapsed: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  width: 100%;
  padding: 0.6rem 0.75rem;
  border-radius: 10px;
  color: #64748b;
  font-weight: 600;
  font-size: 0.92rem;
  text-decoration: none;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: all 150ms ease;
  margin-top: 0.75rem;
  justify-content: ${(props) => (props.$collapsed ? "center" : "flex-start")};

  svg {
    width: 18px;
    height: 18px;
    color: #94a3b8;
    flex-shrink: 0;
    transition: all 150ms ease;
  }

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    opacity: ${(props) => (props.$collapsed ? 0 : 1)};
    width: ${(props) => (props.$collapsed ? "0" : "auto")};
    transition: opacity 150ms ease, width 220ms ease;
  }

  &:hover {
    background: rgba(244, 63, 94, 0.08);
    color: #e11d48;

    svg {
      color: #e11d48;
      transform: scale(1.1);
    }
  }

  &:active {
    transform: scale(0.98);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:hover {
      transform: none;
    }

    &:active {
      transform: none;
    }

    svg {
      transition: none;
    }

    span {
      transition: none;
    }
  }
`;

// ============================================================================
// Mobile Sidebar Styles
// ============================================================================

const MobileSidebarContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1.25rem 0.85rem;
  height: 100%;
`;

const MobileHeader = styled.div`
  padding: 0 0.4rem 1rem;
`;

const MobileLogo = styled.img`
  height: 32px;
  width: auto;
`;

// ============================================================================
// Main Component
// ============================================================================

const AppSidebar = ({
  user,
  showMatches = false,
  showExperiments = false,
  open = false,
  onClose,
  onCollapseChange,
}: AppSidebarProps): JSX.Element => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Load collapse state from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("sidebar-collapsed");
    if (saved !== null) {
      const collapsed = saved === "true";
      setIsCollapsed(collapsed);
      onCollapseChange?.(collapsed);
    }
  }, [onCollapseChange]);

  // Save collapse state to localStorage
  const toggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem("sidebar-collapsed", String(newState));
    onCollapseChange?.(newState);
  };

  const handleSignOut = () => {
    clearTokens();
    redirectToAuthLogout();
  };

  // Get user initials for avatar
  const getInitials = () => {
    if (!user) return "U";
    const firstName = user.firstName || user.name?.split(" ")[0] || "";
    const lastName = user.lastName || user.name?.split(" ")[1] || "";
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || "U";
  };

  // Navigation items configuration
  const coreNavItems = [
    { to: "/", label: "Dashboard", icon: Home },
    { to: "/my-roles", label: "My Roles", icon: FileText },
    { to: "/my-candidates", label: "My Candidates", icon: UserCircle },
    { to: "/messages/roles", label: "Messages", icon: Mail },
    ...(showMatches ? [{ to: "/matches", label: "Matches", icon: UserCheck }] : []),
    ...(showExperiments ? [{ to: "/experiments", label: "Experiments", icon: FlaskConical }] : []),
  ];

  const marketplaceNavItems = [
    { to: "/public-roles", label: "Open Roles", icon: FileText },
    { to: "/public-candidates", label: "Open Candidates", icon: Users },
  ];

  const settingsNavItems = [{ to: "/settings", label: "Settings", icon: Settings }];

  // Render navigation item with tooltip
  const renderNavItem = (item: any, index: number, collapsed: boolean) => {
    const Icon = item.icon;
    const navItem = (
      <SidebarLink
        to={item.to}
        end={item.to === "/"}
        $index={index}
        onClick={() => onClose?.()}
      >
        <Icon />
        <NavLabel $collapsed={collapsed}>{item.label}</NavLabel>
      </SidebarLink>
    );

    if (collapsed) {
      return (
        <Tooltip key={item.to} delayDuration={200}>
          <TooltipTrigger asChild>{navItem}</TooltipTrigger>
          <TooltipContent side="right" sideOffset={12}>
            {item.label}
          </TooltipContent>
        </Tooltip>
      );
    }

    return <React.Fragment key={item.to}>{navItem}</React.Fragment>;
  };

  // Collapse toggle component based on state
  const Toggle = isCollapsed ? CollapseToggleCollapsed : CollapseToggle;

  // Desktop sidebar content
  const desktopSidebar = (
    <DesktopSidebarShell $collapsed={isCollapsed}>
      <Toggle
        onClick={toggleCollapse}
        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {isCollapsed ? <ChevronRight /> : <ChevronLeft />}
      </Toggle>

      <SidebarHeader $collapsed={isCollapsed}>
        <LogoLink to="/">
          <LogoImage src={logo} alt="Role Relay" $collapsed={isCollapsed} $show="full" />
          <LogoImage
            src={logoSymbol}
            alt="Role Relay"
            $collapsed={isCollapsed}
            $show="icon"
          />
        </LogoLink>
      </SidebarHeader>

      <TooltipProvider>
        <SidebarNav>
          <NavSection>
            <SidebarTitle $collapsed={isCollapsed}>Core</SidebarTitle>
            {coreNavItems.map((item, index) => renderNavItem(item, index, isCollapsed))}
          </NavSection>

          <NavSection>
            <SidebarTitle $collapsed={isCollapsed}>Marketplace</SidebarTitle>
            {marketplaceNavItems.map((item, index) =>
              renderNavItem(item, index + coreNavItems.length, isCollapsed)
            )}
          </NavSection>

          <NavSection>
            <SidebarTitle $collapsed={isCollapsed}>Settings</SidebarTitle>
            {settingsNavItems.map((item, index) =>
              renderNavItem(
                item,
                index + coreNavItems.length + marketplaceNavItems.length,
                isCollapsed
              )
            )}
          </NavSection>
        </SidebarNav>
      </TooltipProvider>

      <SidebarFooter>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <UserCard $collapsed={isCollapsed}>
              <UserAvatar>{getInitials()}</UserAvatar>
              <UserInfo $collapsed={isCollapsed}>
                <UserName>{user?.name || user?.firstName || "User"}</UserName>
                <UserEmail>{user?.email || "user@example.com"}</UserEmail>
                {user?.role && <UserRole>{user.role}</UserRole>}
              </UserInfo>
            </UserCard>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" sideOffset={8} className="w-56">
            <DropdownMenuLabel>
              <div className="font-semibold">{user?.name || user?.firstName || "User"}</div>
              <div className="text-xs font-normal text-muted-foreground">
                {user?.email || "user@example.com"}
              </div>
            </DropdownMenuLabel>

            <DropdownMenuSeparator />

            <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="p-0">
              <div className="flex items-center justify-between w-full px-2 py-1.5">
                <span className="text-sm">Theme</span>
                <ThemeToggle />
              </div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>

      <TooltipProvider>
        <LogoutSection>
          {isCollapsed ? (
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                <LogoutButton $collapsed={isCollapsed} onClick={handleSignOut}>
                  <LogOut />
                  <span>Logout</span>
                </LogoutButton>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={12}>
                Logout
              </TooltipContent>
            </Tooltip>
          ) : (
            <LogoutButton $collapsed={isCollapsed} onClick={handleSignOut}>
              <LogOut />
              <span>Logout</span>
            </LogoutButton>
          )}
        </LogoutSection>
        {!isCollapsed && <Footer />}
      </TooltipProvider>
    </DesktopSidebarShell>
  );

  // Mobile sidebar content
  const mobileSidebar = (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent side="left" className="w-[75vw] max-w-[300px] p-0">
        <MobileSidebarContent>
          <MobileHeader>
            <MobileLogo src={logo} alt="Role Relay" />
          </MobileHeader>

          <SidebarNav>
            <NavSection>
              <SidebarTitle $collapsed={false}>Core</SidebarTitle>
              {coreNavItems.map((item, index) => renderNavItem(item, index, false))}
            </NavSection>

            <NavSection>
              <SidebarTitle $collapsed={false}>Marketplace</SidebarTitle>
              {marketplaceNavItems.map((item, index) =>
                renderNavItem(item, index + coreNavItems.length, false)
              )}
            </NavSection>

            <NavSection>
              <SidebarTitle $collapsed={false}>Settings</SidebarTitle>
              {settingsNavItems.map((item, index) =>
                renderNavItem(
                  item,
                  index + coreNavItems.length + marketplaceNavItems.length,
                  false
                )
              )}
            </NavSection>
          </SidebarNav>

          <SidebarFooter style={{ marginTop: "auto" }}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <UserCard $collapsed={false}>
                  <UserAvatar>{getInitials()}</UserAvatar>
                  <UserInfo $collapsed={false}>
                    <UserName>{user?.name || user?.firstName || "User"}</UserName>
                    <UserEmail>{user?.email || "user@example.com"}</UserEmail>
                    {user?.role && <UserRole>{user.role}</UserRole>}
                  </UserInfo>
                </UserCard>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" sideOffset={8} className="w-56">
                <DropdownMenuLabel>
                  <div className="font-semibold">{user?.name || user?.firstName || "User"}</div>
                  <div className="text-xs font-normal text-muted-foreground">
                    {user?.email || "user@example.com"}
                  </div>
                </DropdownMenuLabel>

                <DropdownMenuSeparator />

                <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="p-0">
                  <div className="flex items-center justify-between w-full px-2 py-1.5">
                    <span className="text-sm">Theme</span>
                    <ThemeToggle />
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarFooter>

          <LogoutSection>
            <LogoutButton $collapsed={false} onClick={handleSignOut}>
              <LogOut />
              <span>Logout</span>
            </LogoutButton>
          </LogoutSection>
          <Footer/>
        </MobileSidebarContent>
      </SheetContent>
    </Sheet>
  );

  return (
    <>
      {desktopSidebar}
      {mobileSidebar}
    </>
  );
};

export default AppSidebar;
